import { useQuery } from '@tanstack/react-query';
import { http as atsHttps } from '@/lib/ats-https';
import { useKFlags } from '@/hooks/use-k-flags';
import { getStaticSection } from '@/lib/staticFieldOrder';

/** Source and Credited to each have their own row in `sys/contact`. */
const SOURCE_FIELD_ID = 'source';
const CREDITED_TO_FIELD_ID = 'credited_to';
const PERSONAL_INFO_SECTION_ID = 'personal_info';

/** How one of the two fields is configured. */
export interface AttributionFieldState {
  isHidden: boolean;
  isRequired: boolean;
}

export interface AttributionFieldConfig {
  source: AttributionFieldState;
  creditedTo: AttributionFieldState;
  /**
   * Neither field is rendered. Callers that drop the whole Attribution block
   * (heading included) test this rather than the two separately, since a
   * heading over nothing is the bug. Secondary surfaces always render both
   * fields today, so this is false; it stays so the callers need not change
   * if a field ever can be hidden here again.
   */
  isHidden: boolean;
  /**
   * Show the "Change source and credited to" switch.
   *
   * The switch can turn BOTH fields off, so it may only appear while neither
   * is Mandatory — otherwise it contradicts a required field. Dropped once
   * both are Hidden too, when there is nothing left for it to toggle.
   */
  showToggle: boolean;
  isLoading: boolean;
}

/**
 * The single source of truth for how Source / Credited to behave, derived from
 * their two rows on `settings/candidate-fields`.
 *
 * Six surfaces render these two fields (create-contact drawer, Add to job,
 * Transfer to job, the candidate edit drawer, the profile fields and the filled
 * application form). Re-deriving the rule in each is how they drift apart, so
 * every one of them reads it from here.
 *
 * Shares the create-contact drawer's query key deliberately: same request, one
 * cache entry, and the settings page's write already evicts it.
 */
export const useAttributionFieldConfig = (): AttributionFieldConfig => {
  const { feJobCandidateFieldReorder } = useKFlags();

  const { data, isLoading } = useQuery({
    queryKey: ['contacts/custom_fields', 'contact'],
    // Calls the transport directly rather than formBuilder's fetchSelectOptions:
    // that helper is a plain HTTP wrapper, but it lives under components/, and a
    // hook importing from there trips the boundaries rule.
    queryFn: async () => {
      const response = await atsHttps.get(
        'ats_custom_fields/form_fields?subject_type=contact'
      );
      return response?.data;
    },
    staleTime: 0,
    // Nothing reads the configuration while the flag is off, so do not pay for
    // the request either.
    enabled: Boolean(feJobCandidateFieldReorder),
  });

  // Flag off: the pre-reorder behaviour, where the toggle always shows and
  // neither field is ever required or hidden.
  if (!feJobCandidateFieldReorder) {
    const visible = { isHidden: false, isRequired: false };
    return {
      source: visible,
      creditedTo: visible,
      isHidden: false,
      showToggle: true,
      isLoading: false,
    };
  }

  const section = getStaticSection(
    data?.ats_form_template?.static_fields,
    PERSONAL_INFO_SECTION_ID
  );

  // An account that has not been backfilled has neither row. Fall back to
  // today's behaviour rather than hiding the fields outright.
  const stateOf = (id: string): AttributionFieldState => {
    const row = section.find((field: any) => field?.id === id);
    // Every surface this hook drives is a SECONDARY one -- Add to job,
    // Transfer to job, the candidate edit drawer. A row's status binds only
    // the create-contact form it is configured on, so here the field is always
    // shown and, by default, optional: an admin hiding Source on that form
    // must not strip it from Add to job.
    //
    // The one way to make it bind here is the row's "Make Mandatory
    // everywhere candidates are added" checkbox, which means exactly that:
    // Mandatory, whatever the create-form status is -- Hide included
    // (CORE-5352). Without that opt-in an admin marking Source mandatory for
    // new contacts would start failing Add to job for every existing one.
    return { isHidden: false, isRequired: Boolean(row?.enforced_everywhere) };
  };

  const source = stateOf(SOURCE_FIELD_ID);
  const creditedTo = stateOf(CREDITED_TO_FIELD_ID);
  const isHidden = source.isHidden && creditedTo.isHidden;

  return {
    source,
    creditedTo,
    isHidden,
    showToggle: !isHidden && !source.isRequired && !creditedTo.isRequired,
    isLoading,
  };
};
